// 3D-only additions to the Paint Tool panel, shown while the Paint Tool paints
// a 3D model (the brush, media and pigment controls are the same as in 2D).
// The model's surface belongs to the model, not to the 2D document's paper.
// Talks to the 3D viewport through vx-paint3d-state / -set / -query events.

import React, { useEffect, useState } from 'react';

const noteStyle = { fontSize: 10, lineHeight: 1.45, color: 'rgba(255,255,255,0.5)' };

export function usePaint3dState() {
  const [state, setState] = useState({ active: false });
  useEffect(() => {
    const onState = (event) => setState(event && event.detail ? event.detail : { active: false });
    window.addEventListener('vx-paint3d-state', onState);
    window.dispatchEvent(new CustomEvent('vx-paint3d-query'));
    return () => window.removeEventListener('vx-paint3d-state', onState);
  }, []);
  return state;
}

const send = (patch) => window.dispatchEvent(new CustomEvent('vx-paint3d-set', { detail: patch }));

export default function ModelSurfaceControls({ SelectRow, SliderRow }) {
  const state = usePaint3dState();
  if (!state.active) return null;
  const stats = state.stats;
  return (
    <div data-testid="paint3d-controls" style={{ display: 'grid', gap: 10, minWidth: 0, padding: '8px 0', borderTop: '1px solid rgba(255,255,255,0.08)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
      <SelectRow
        label="Model surface"
        value={state.surface}
        options={(state.surfaces || []).map((s) => ({ id: s.id, label: s.label }))}
        onChange={(v) => send({ surface: v })}
      />
      <SelectRow
        label="Paintable material"
        value={String(state.paintableMaterial ?? -1)}
        options={[{ id: '-1', label: 'All materials' }, ...(state.materials || []).map((m) => ({ id: String(m.id), label: m.label }))]}
        onChange={(v) => send({ paintableMaterial: Number(v) })}
      />
      <SliderRow label="Physical brush scale" value={Math.round((state.brushScale ?? 1) * 100)} min={10} max={400} suffix="%" onChange={(v) => send({ brushScale: v / 100 })} />
      <div style={noteStyle}>
        UV channel: UV 0 (the 3D paint atlas).
        {stats ? ` ${stats.islands} UV island${stats.islands === 1 ? '' : 's'}, paint grid ${stats.grid}.` : ' The paint grid is created at the first stroke.'}
        {stats && stats.overlapFraction > 0.001 ? ` ${Math.round(stats.overlapFraction * 100)}% of the atlas has overlapping UVs: paint there also appears where the UVs overlap.` : ''}
        {stats && stats.outOfRangeTriangles ? ` ${stats.outOfRangeTriangles} triangles have UVs outside 0–1 and cannot be painted.` : ''}
      </div>
    </div>
  );
}
